import React, { useMemo, useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { apiAppFetch } from '../utils/apiFetch';
import { Search, Clear, CheckCircle } from '@mui/icons-material';
import ScriptIcon from '../components/ScriptIcon';
import './../AccountPages.css';

const EXCHANGES = [
  { value: 'ALL', label: 'All' },
  { value: 'FUTCOM', label: 'MCX FUT' },
  { value: 'FUTSTK', label: 'NSE FUT' },
  { value: 'OPTSTK', label: 'NSE OPT' }
];

const tagClass = (segment) => {
  if (segment === 'FUTCOM') return 'mcx';
  if (segment === 'FUTSTK') return 'nse';
  if (segment === 'OPTSTK') return 'opt';
  return '';
};

const BlockedScripts = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [blockedScripts, setBlockedScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterExchange, setFilterExchange] = useState('ALL');

  const getBlockedSymbols = async () => {
    try {
      setLoading(true);
      const response = await apiAppFetch('getBlockedSymbols', {
        method: 'POST',
        body: JSON.stringify({}),
      });

      if (response.status === 401) {
        navigate('/login', { replace: true });
        return;
      }

      const data = await response.json();
      if (Array.isArray(data)) setBlockedScripts(data);
      else if (data && typeof data === 'object') setBlockedScripts(Object.values(data));
      else setBlockedScripts([]);
    } catch (error) {
      setBlockedScripts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBlockedSymbols();
  }, []);

  const filteredScripts = blockedScripts.filter((script) => {
    if (!script) return false;
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      (script.dispname && script.dispname.toLowerCase().includes(q)) ||
      (script.symbol && script.symbol.toLowerCase().includes(q)) ||
      (script.instrumentIdentifier && script.instrumentIdentifier.toLowerCase().includes(q)) ||
      (script.lotVariant && script.lotVariant.toLowerCase().includes(q));
    const matchesExchange = filterExchange === 'ALL' || script.segment === filterExchange;
    return matchesSearch && matchesExchange;
  });

  const grouped = useMemo(() => {
    const map = {};
    filteredScripts.forEach((script) => {
      const key = script.segment || 'OTHER';
      if (!map[key]) map[key] = [];
      map[key].push(script);
    });
    return map;
  }, [filteredScripts]);

  const getExchangeLabel = (segment) => {
    const found = EXCHANGES.find((ex) => ex.value === segment);
    return found ? found.label : segment;
  };

  return (
    <div className={`account-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      <div className="account-search">
        <Search className="account-search-icon" />
        <input
          type="text"
          placeholder="Search script"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery ? (
          <button className="account-search-clear" onClick={() => setSearchQuery('')} type="button">
            <Clear fontSize="small" />
          </button>
        ) : null}
      </div>

      <div className="account-chips">
        {EXCHANGES.map((exchange) => (
          <button
            key={exchange.value}
            type="button"
            className={`account-chip ${filterExchange === exchange.value ? 'active' : ''}`}
            onClick={() => setFilterExchange(exchange.value)}
          >
            {exchange.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="account-loading">
          <div className="account-spinner" />
          <p>Loading blocked scripts...</p>
        </div>
      ) : filteredScripts.length === 0 ? (
        <div className="account-empty">
          <CheckCircle />
          <h3>No blocked scripts</h3>
          <p>
            {blockedScripts.length === 0
              ? 'Nothing is blocked on this account.'
              : 'No scripts match the current filters.'}
          </p>
        </div>
      ) : (
        Object.keys(grouped).map((segment) => (
          <section key={segment} className="account-group">
            <div className="account-group-title">
              <span>{getExchangeLabel(segment)}</span>
              <span>{grouped[segment].length}</span>
            </div>
            <div className="account-panel">
              {grouped[segment].map((script, index) => (
                <div
                  key={`${script.instrumentIdentifier || script.symbol || 'script'}-${segment}-${script.lotVariant || 'MEGA'}-${index}`}
                  className="account-row account-row-script"
                >
                  <div className="account-row-main script-with-icon">
                    <ScriptIcon name={script.dispname} symbol={script.symbol} size={28} />
                    <div className="script-identity-text">
                      <div className="account-row-name">
                        {script.dispname}
                        {String(script.lotVariant || '').toUpperCase() === 'MINI' && (
                          <span className="lot-variant-mini">MINI</span>
                        )}
                      </div>
                      <div className="account-row-meta">{script.symbol || script.instrumentIdentifier}</div>
                    </div>
                  </div>
                  <span className={`account-tag ${tagClass(segment)}`}>
                    {getExchangeLabel(segment)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
};

export default BlockedScripts;
